import {
  BerthResponseDto,
  BerthSide,
  CargoType,
  CompanyResponseDto,
  PierResponseDto,
  VesselDto,
  VesselVisitDto,
} from '@planning/app/api'
import { FieldStore, FormStore } from '@tom-ui/utils'
import dayjs from 'dayjs'
import { action, makeObservable, observable, runInAction } from 'mobx'
import { VesselVisitBerthAndTimingFormStoreType } from './VesselVisitBerthAndTimingFormStore'
import { VesselVisitCargoFormStoreType } from './VesselVisitCargoFormStore'
import {
  VesselVisitGeneralInformationFormStoreType,
  VoyageDto,
} from './VesselVisitGeneralInformationFormStore'

export type VesselVisitFormStoreType = {
  // generalInformation, cargo, and berthAndTiming are optional??
  generalInformation?: VesselVisitGeneralInformationFormStoreType
  cargo?: VesselVisitCargoFormStoreType
  berthAndTiming?: VesselVisitBerthAndTimingFormStoreType
  notes?: string | null
}

const buildInitialValue = (
  initialData: VesselVisitFormStoreInitialData,
): VesselVisitFormStoreType => {
  const vesselVisit = initialData.vesselVisit
  const vessel = initialData.vessels?.[0]
  const generalInformation: VesselVisitGeneralInformationFormStoreType = {
    // todo: should we remove
    vesselIds: vesselVisit?.carrierIds,
    vessel: vessel,
    vesselOperator: initialData.vesselOperator,
    shippingAgent: initialData.shippingAgent,
    portCallId: vesselVisit?.portCallId,
    voyages: initialData.voyages,
    vesselServiceId: vesselVisit?.vesselServiceId ?? null,
    portRotation: vesselVisit?.portRotation ?? [],
    visitNumber: vesselVisit?.number,
    vesselNotes: vessel?.remarks ?? '',
  }

  const cargo: VesselVisitCargoFormStoreType = {
    cargoType: vesselVisit?.cargoType ?? CargoType.Container,
    dischargeEstimate: vesselVisit?.dischargeEstimate ?? 0,
    loadEstimate: vesselVisit?.loadEstimate ?? 0,
    restowEstimate: vesselVisit?.restowEstimate ?? 0,
  }

  const eta = vesselVisit?.eta ? dayjs(vesselVisit.eta) : null
  const etb = vesselVisit?.etb ? dayjs(vesselVisit.etb) : null
  const etd = vesselVisit?.etd ? dayjs(vesselVisit.etd) : null
  const berthAndTiming: VesselVisitBerthAndTimingFormStoreType = {
    etaDate: eta,
    etaTime: eta,
    etbDate: etb,
    etbTime: etb,
    etdDate: etd,
    etdTime: etd,
    length: vessel?.length ?? null,
    draftArrival: vesselVisit?.importDraft ?? null,
    draftDeparture: vesselVisit?.exportDraft ?? null,
    dockingSide: vesselVisit?.berthSide ?? BerthSide.Port,
    qmmFrom: vesselVisit?.qmmFrom ?? null,
    qmmTo: vesselVisit?.qmmTo ?? null,
    visitType: null,
    schedule: null,
    pier: initialData.pier ?? null,
    berths: initialData.berths ?? null,
  }

  return { generalInformation, cargo, berthAndTiming, notes: vesselVisit?.notes }
}

export type VesselVisitFormStoreInitialData = {
  vesselVisit?: VesselVisitDto
  vessels?: VesselDto[]
  vesselOperator?: CompanyResponseDto
  shippingAgent?: CompanyResponseDto
  voyages?: VoyageDto[]
  pier?: PierResponseDto | null
  berths?: BerthResponseDto[] | null
}

export class VesselVisitFormStore extends FormStore<VesselVisitFormStoreType> {
  id?: number
  notes: FieldStore<string>

  // todo: update test
  constructor(initialData?: VesselVisitFormStoreInitialData) {
    if (initialData) {
      const initialValues = buildInitialValue(initialData)
      super(initialValues)
    } else {
      super({})
    }

    makeObservable(this, {
      id: observable,
      patchData: action,
    })

    runInAction(() => {
      this.id = initialData?.vesselVisit?.id
    })

    this.notes = new FieldStore(initialData?.vesselVisit?.notes ?? '')
  }

  patchData(data: Partial<VesselVisitFormStoreType>) {
    this.notes.setValue(data.notes ?? '')
  }

  get data(): VesselVisitFormStoreType {
    return {
      ...super.data,
      notes: this.notes.value,
    }
  }

  // manage state here - data, loading
}
